import { Intent } from '@blueprintjs/core';
import { ButtonSave } from 'components/buttons';
import { FormSuggest, FormDateInput, FormLocationSelector } from 'components/form-fields';
import { AlertUnsavedData, NotificationToaster, NotificationInline } from 'components/notifications';
import { useDirtyData } from 'hooks/useDirtyData';
import React, { useEffect, useState, Fragment } from 'react';
import { JobService, UserService, AssetService } from 'services';
import { UserAccess, UserHasAccess } from 'components/user-access/UserAccess';
import { DataDisplayTable } from "components/data-display";
import axios from 'axios';
import moment from "moment";
import { useValidation } from "hooks/useValidation";
import { ShowHide } from "components/layout";
import { PageRestricted } from 'pages/errors/page-restricted';
import { HelperFunctions } from 'helpers';

export function JobDetails(props) {
    const defaultJobDetails = {
        jobId: null,
        state: null,
        productName: null,
        productDescription: null,
        intendedAssetId: null,
        assetType: null,
        locationId: null,
        dueDate: null,
        scheduledDate: null,
        flsStatus: null,
        jobNote: null,
        jobProductBusinessAreaId: null,
        completed:null
    };

    const [isValid, errors, validate] = useValidation();
    const [jobDetails, setJobDetails] = useState(defaultJobDetails);
    const [originalJobDetails, setOriginalJobDetails] = useState(defaultJobDetails);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [locationList, setLocationList] = useState({ location: [], hierarchy: [] });
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [assets, setAssets] = useState([]);
    const [assetSearch, setAssetSearch] = useState('');
    const isDataDirty = useDirtyData(originalJobDetails, jobDetails);
    const [canSave, setCanSave] = useState(false);
    const [amendmentRequest, setAmendmentRequest] = useState(false);
    const monthsInPastDefault = 240;
    const minDateDefault = moment().subtract(monthsInPastDefault, 'months').toDate();
    const [minIntendedDate, setMinIntendedDate] = useState(minDateDefault);
    const [showLocationsAsHierarachy, setShowLocationsAsHierarachy] = useState(true);
    const [headerSections, setHeaderSections] = useState([]);
    const isInternal = UserHasAccess("Internal");
    const disableEdit = true;
    const requiredAction = ["ScheduledAppointment:View", "Internal"];

    useEffect(initialLoad, [props.job]);

    function initialLoad() {

        var serviceCalls = [UserService.getLoggedInUserLocations(1, false, true)];

        if (props.job.intendedAssetId) {
            serviceCalls.push(AssetService.getAsset(props.job.intendedAssetId));
        }

        axios.all(serviceCalls).then(axios.spread(function (...serviceCallsResponse) {

            var locationPermissionsResponse = serviceCallsResponse[0];
            var assetResponse = serviceCallsResponse.length > 1 ? serviceCallsResponse[1] : null;

            searchAsset(assetResponse !== null ? assetResponse?.registration : '');            
            setJobDetails(props.job);
            setOriginalJobDetails(props.job);
            setLocationList(locationPermissionsResponse);
            setSelectedLocations([props.job.locationId]);
            setCanSave(props.job.jobProductBusinessAreaId === 1 && isInternal && !props.job.completed && props.job.scheduledDate === null);
            
            var amendment = props.job.amendmentType != null && (props.job.amendmentType === "Amendment" || props.job.amendmentType === "Cancellation");

            setAmendmentRequest(amendment);

            var headers = [
                {
                    header: isInternal ? "Product Name" : "Product Details",
                    value: isInternal ? props.job.productName : props.job.productDescription,
                    key: 'product-type'
                },
                {
                    header: isInternal && props.job.pubsJobId != null ? "Job ID / PUBS ID" : "Job ID",
                    value: isInternal && props.job.pubsJobId != null ? props.job.jobId + " / " + props.job.pubsJobId : props.job.jobId,
                    key: 'jod-id'
                },
                {
                    header: "Schedule Date",
                    value: props.job.scheduledDate !== null ? moment(props.job.scheduledDate).format("DD/MM/YYYY") : '',
                    key: 'scheduled-date'
                },
                {
                    header: "Job Status",
                    value: props.job.state,
                    key: 'job-status'
                },
                {
                    header: "Business Area",
                    value: props.job.jobProductBusinessArea,
                    key: 'job-business-area'
                },
            ];

            if (amendment) {
                var amendByText = "Amendment By";
                var amendDateText = "Amendment Date";

                if (props.job.amendmentType === "Cancellation") {
                    amendByText = "Cancellation By";
                    amendDateText = "Cancellation Date";
                }

                headers.unshift({
                    header: amendByText,
                    value: props.job.amendmentBy,
                    key: 'job-amendment-by'
                });
                headers.unshift({
                    header: amendDateText,
                    value: props.job.amendmentDate !== null ? moment(props.job.amendmentDate).format("DD/MM/YYYY") : '',
                    key: 'job-amendment-date'
                });
            }

            setHeaderSections(headers);
            setLoading(false);
        }), () => {
            NotificationToaster.show(Intent.DANGER, "Could not job details. Please refresh the page.");
        });
    }

    useEffect(() => {
        if (jobDetails.jobId === null) {
            return;
        }
        var minDate = moment().toDate();        
        if (jobDetails.dueDate !== null) {
            var dueDate = moment(jobDetails.dueDate).toDate();
            if (dueDate < minDate) {
                minDate = dueDate;
            }                
        }
        setMinIntendedDate(canSave ? minDate : minDateDefault);

    }, [canSave, jobDetails]);

    function saveJobDetails() {
        setSaving(true);

        JobService.saveJobDetails(jobDetails)
            .then(
                () => {
                    setOriginalJobDetails(jobDetails);
                    NotificationToaster.show(Intent.SUCCESS, "job details updated successfully.");
                    setSaving(false);
                },
                () => {
                    NotificationToaster.show(Intent.DANGER, "Could not save job details. Please try again.");
                    setSaving(false);
                },
        );

        HelperFunctions.executeIfNotNull(props.onSave, jobDetails);
    }

    function onDueDateChange(item) {
        setJobDetails((previousValue) => {
            return {
                ...previousValue,
                dueDate: item ? moment(item).format("YYYY-MM-DD") : null
            }
        });
    }

    function onLocationSelect(locations) {

        var lastLocation = locations.length > 0 ? locations.pop() : null;

        setJobDetails((previousValue) => {
            return {
                ...previousValue,
                locationId: lastLocation
            }
        });

        setSelectedLocations(lastLocation == null ? [] : [lastLocation]);
    }

    function onRegistationChange(item) {
        setJobDetails((previousValue) => {
            return {
                ...previousValue,
                intendedAssetId: item.id
            }
        });
    }

    function setUseHierarchy() {
        setShowLocationsAsHierarachy(previous => !previous);
    }

    useEffect(validateModel, [jobDetails])

    function validateModel() {
        let rules = [{ fieldName: "locationId", required: true }];

        if (isInternal) {
            rules.push({ fieldName: "dueDate", required: true, });
        }

        validate(rules, jobDetails);
    }

    function formatAssets(assetList) {
        return assetList.map(asset => {
            return {
                id: asset.assetId,
                name: asset.assetRegistration
            };
        });
    }

    function searchAsset(searchText) {
        const assetsRequest = {
            pageNumber: 1,
            searchTerm: searchText,
            filters: []
        };

        AssetService.searchAssets(assetsRequest).then(function (assetResponse) {
            setAssets(formatAssets(assetResponse.data));
        });
    }

    useEffect(function () {
        //On inital load isPostCodeValid is set to true, so we'll add the length check
        if (assetSearch.length > 0) {
            searchAsset(assetSearch);
        }
    }, [assetSearch])

    function onAssetSearchTextChange(event) {
        setAssetSearch(event);
    }

    return (

        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="row">
                        <h2>Job Details</h2>
                        <ShowHide
                            evaluator={amendmentRequest}
                            show={(
                                <NotificationInline show={true} allowClose={false} intent="warning" text={props.job.amendmentType === "Amendment" ? "Re-schedule requested" : "Cancellation requested"} />
                            )}
                        />
                        <DataDisplayTable
                            data={headerSections}
                            displayInRows={false}
                            loading={loading}
                        />
                        <UserAccess perform={["Internal"]}
                            yes={() => (
                                <Fragment>
                                    <FormDateInput
                                        id="field-asset-start-date"
                                        disabled={saving || disableEdit || !canSave}
                                        headingText="Due date:"
                                        onChange={onDueDateChange}
                                        value={jobDetails.dueDate}
                                        loading={loading}
                                        dangerHelperText={errors.dueDate}
                                        minDate={minIntendedDate}
                                    ></FormDateInput>
                                </Fragment>
                            )}
                        />
                        <FormLocationSelector
                            headingText={`Visit Location:`}
                            dangerHelperText={errors["location"]}
                            businessArea={"Tacho"}
                            loading={loading}
                            selectedLocations={selectedLocations}
                            setSelectedLocations={onLocationSelect}
                            useHierarchy={showLocationsAsHierarachy}
                            setUseHierarchy={setUseHierarchy}
                            useLocationDefaults={false}
                            useCustomLocations
                            customLocations={locationList}
                            locationDefaultsToTrue
                            singleSelection
                            disabled={saving || disableEdit || !canSave}
                        />
                        <ShowHide
                            evaluator={jobDetails.jobProductBusinessAreaId === 2}
                            show=
                            {
                                <FormSuggest
                                    onQueryChange={onAssetSearchTextChange}
                                    items={assets}
                                    selectedValue={jobDetails.intendedAssetId}
                                    loading={loading}
                                    onItemSelect={onRegistationChange}
                                    headingText="Registration"
                                    dangerHelperText={errors.intendedAssetId}
                                    disabled={saving || disableEdit || !canSave}
                                    clearSelection={false}
                                />
                            }
                        >
                        </ShowHide>

                        <AlertUnsavedData
                            isDirty={isDataDirty}>
                        </AlertUnsavedData>
                        <ShowHide
                            evaluator={canSave && !disableEdit}
                            show=
                            {
                                <ButtonSave
                                    onClick={saveJobDetails}
                                    disabled={saving}
                                    simpleDisabled={!isValid}
                                    loading={loading}
                                ></ButtonSave>
                            }
                        >
                        </ShowHide>

                    </div>
                </Fragment>

            )}
            no={() => (
                <PageRestricted />
            )}
        />

    );
}